import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Box, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import coreUtils from "../../utils/coreUtils.js";
import * as monaco from 'monaco-editor';
import { MixpanelService } from "utils/mixpanelService";

import "../../styles/components/Functions/FunctionModal.scss";
import MonacoEditorWithFullscreen from "components/Common/MonacoEditorWithFullscreen.jsx";
import { useTheme } from "contexts/ThemeContext.jsx";

// Define custom themes for JavaScript
monaco.editor.defineTheme("js-theme-light", {
  base: 'vs',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '0000FF' },      // Blue for keywords
    { token: 'string', foreground: 'A31515' },       // Red for strings
    { token: 'number', foreground: '098658' },       // Green for numbers
    { token: 'comment', foreground: '008000' },      // Green for comments
    { token: 'function', foreground: '795E26' },     // Brown for functions
    { token: 'variable', foreground: '001080' },     // Dark blue for variables
    { token: 'operator', foreground: '000000' },     // Black for operators
    { token: 'delimiter', foreground: '000000' },    // Black for delimiters
    { token: 'bracket', foreground: '000000' },      // Black for brackets
  ],
  colors: {
    'editor.background': '#ffffff',
    'editor.foreground': '#000000',
    'editorLineNumber.foreground': '#2B91AF',
    'editor.lineHighlightBackground': '#f0f0f0',
    'editor.lineHighlightBorder': '#f0f0f0',
    'editorGutter.background': '#f3f3f3',
    'editor.selectionBackground': '#add6ff',
    'editor.inactiveSelectionBackground': '#e5ebf1',
    'editorCursor.foreground': '#000000',
    'editorWhitespace.foreground': '#A0A0A0',
    'editorIndentGuide.background': '#D3D3D3',
    'editorIndentGuide.activeBackground': '#939393',
    'editorLineNumber.activeForeground': '#000000',
    'editor.selectionHighlightBackground': '#add6ff',
    'editor.wordHighlightBackground': '#575757',
    'editor.wordHighlightStrongBackground': '#004972',
  }
});

monaco.editor.defineTheme("js-theme-dark", {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '569CD6' },      // Light blue for keywords
    { token: 'string', foreground: 'CE9178' },       // Light red for strings
    { token: 'number', foreground: 'B5CEA8' },       // Light green for numbers
    { token: 'comment', foreground: '6A9955' },      // Light green for comments
    { token: 'function', foreground: 'DCDCAA' },     // Light yellow for functions
    { token: 'variable', foreground: '9CDCFE' },     // Light blue for variables
    { token: 'operator', foreground: 'D4D4D4' },     // Light gray for operators
    { token: 'delimiter', foreground: 'D4D4D4' },    // Light gray for delimiters
    { token: 'bracket', foreground: 'D4D4D4' },      // Light gray for brackets
  ],
  colors: {
    'editor.background': '#1E1E1E',
    'editor.foreground': '#D4D4D4',
    'editorLineNumber.foreground': '#858585',
    'editor.lineHighlightBackground': '#2A2D2E',
    'editor.lineHighlightBorder': '#2A2D2E',
    'editorGutter.background': '#1E1E1E',
    'editor.selectionBackground': '#264F78',
    'editor.inactiveSelectionBackground': '#3A3D41',
    'editorCursor.foreground': '#FFFFFF',
    'editorWhitespace.foreground': '#404040',
    'editorIndentGuide.background': '#404040',
    'editorIndentGuide.activeBackground': '#707070',
    'editorLineNumber.activeForeground': '#FFFFFF',
    'editor.selectionHighlightBackground': '#264F78',
    'editor.wordHighlightBackground': '#575757',
    'editor.wordHighlightStrongBackground': '#004972',
  }
});

const FunctionModal = ({ isOpen, onClose, actionType, onConfirm, detail = {} }) => {
  const { isDarkMode } = useTheme();

  const [name, setName] = useState(detail?.function_name || "");
  const [args, setArgs] = useState(detail?.fuction_args || "");
  const [code, setCode] = useState(detail?.js_code || "");
  const [description, setDescription] = useState(detail?.description || "");
  const [errors, setErrors] = useState({});
  const isLoading = useSelector(state => state.function.isLoading);
  const nameRef = useRef(detail?.name);

  useEffect(() => {
    setName(detail?.function_name);
    setArgs(detail?.function_args);
    setCode(detail?.js_code);
    setDescription(detail?.description);
    nameRef.current = detail?.function_name;
  }, [detail]);

  useEffect(() => {
    if (isOpen) MixpanelService.track("function_modal_opened", { action_type: actionType });
  }, [isOpen, actionType]);

  const handleConfirmClick = (e) => {
    const errorsObj = validate();
    if (Object.keys(errorsObj).length > 0) {
      return;
    }
    if (actionType === 'create' || actionType === 'duplicate') {
      MixpanelService.track("js_function_created", {});
    } else {
      MixpanelService.track("js_function_updated", {});
    }
    onConfirm(actionType, {
      name: name?.trim(),
      args: args?.trim(),
      code: code,
      description: description?.trim(),
    });
  }

  const handleClose = () => {
    MixpanelService.track("function_modal_closed", { action_type: actionType });
    setName("");
    setArgs("");
    setCode("");
    setDescription("");
    setErrors({});
    onClose();
  }

  const handleChange = (name, value) => {
    if (name === "name") setName(value);
    else if (name === "args") setArgs(value);
    else if (name === "description") setDescription(value);
    if (errors.name || errors.args) validate();
  }

  const handleCodeChange = (code) => {
    setCode(code);
    if (code?.trim()) setErrors(prevState => ({ ...prevState, code: "" }));
  }

  const validate = () => {
    const errors = {};
    const nameReg = RegExp(/^[a-zA-Z0-9]+$/);
    let nameError = coreUtils.isStringInvalidOrBlank(name) ? "Required" : "";
    if (!nameError) {
      nameError = nameReg.test(name.trim()) ? "" : "Invalid function name, should only consist of [a-z,A-Z,0-9]";
    }
    if (!nameError && actionType === "duplicate") {
      nameError = name?.trim() === nameRef.current ? "Function name already exist" : "";
    }
    if (nameError) errors.name = nameError;
    // errors.args = coreUtils.isStringInvalidOrBlank(args) ? "" : ""
    if (coreUtils.isStringInvalidOrBlank(code))
      errors.code = "Function code should be a valid expression.";
    setErrors(errors);
    return errors;
  }

  if (!isOpen) {
    return null;
  }

  let title = "";
  let btnText = "";

  if (actionType === "create" || actionType === "duplicate") {
    title = actionType === "create" ? "Create JS function" : "Duplicate JS function";
    btnText = "Create";
  } else {
    title = "Edit JS function";
    btnText = "Update";
  }

  return (
    <Dialog maxWidth="sm" fullWidth onClose={(e) => { e.preventDefault(); }} open={isOpen} className="functionModal">
      <DialogTitle><h4>{title}</h4></DialogTitle>
      <DialogContent>
        <h6>Name <span style={{ fontSize: "12px" }}>[a-z,A-Z,0-9]</span></h6>
        <TextField value={name} size="small" fullWidth onChange={(e) => { handleChange("name", e.target.value) }} placeholder="name" error={!!errors.name} />
        {errors.name && <span className="error">{errors.name}</span>}
        <h6>Arguments</h6>
        <TextField value={args} size="small" fullWidth onChange={(e) => { handleChange("args", e.target.value) }} placeholder="arguments" error={!!errors.args} />
        {/* {errors.args && <span className="error">{errors.args}</span>} */}
        <h6>Description</h6>
        <TextField value={description} size="small" fullWidth onChange={(e) => { handleChange("description", e.target.value) }} placeholder="description" error={!!errors.description} />
        <h6>Code</h6>
        <Box sx={{ border: "1px solid var(--borderColor)", borderRadius: "4px", p: 1 }}>
          <Typography sx={{ mb: "4px", color: "var(--text-secondary)", fontWeight: 550 }}>function {name}({args}) {"{"}</Typography>
          <MonacoEditorWithFullscreen
            className="jsEditor"
            language="javascript"
            theme={isDarkMode ? "js-theme-dark" : "js-theme-light"}
            value={code}
            height="120px"
            onChange={(value) => { handleCodeChange(value) }}
            style={{ borderBottom: "1px solid var(--borderColor)" }}
            options={{
              automaticLayout: true,
              wordWrap: "on",
              minimap: { enabled: false },
              lineNumbers: "on",
              tabSize: 2,
              fontSize: 13,
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              scrollbar: {
                vertical: 'visible',
                horizontal: 'visible',
                verticalScrollbarSize: 14,
                horizontalScrollbarSize: 14,
                verticalSliderSize: 14,
                horizontalSliderSize: 14,
                useShadows: false,
                arrowSize: 14,
              },
              overviewRulerBorder: false,
              hideCursorInOverviewRuler: true,
              renderLineHighlight: 'all',
              matchBrackets: 'always',
              autoClosingBrackets: 'always',
              autoClosingQuotes: 'always',
              formatOnPaste: true,
              formatOnType: true,
            }}
          />
          <Typography sx={{ color: "var(--text-secondary)", fontWeight: 550 }}>{"}"}</Typography>
        </Box>
        {errors.code && <span className="error">{errors.code}</span>}
      </DialogContent>
      <DialogActions sx={{ my: 2, p: "0px 24px" }}>
        <Button disabled={isLoading} onClick={handleClose} variant="outlined" color="error">Discard</Button>
        <Button disabled={isLoading} onClick={handleConfirmClick} variant="contained" color="primary">
          {btnText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FunctionModal;